import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PrimaryButton } from '../../../../components/common/buttons/PrimaryButton';
import { SecondaryButton } from '../../../../components/common/buttons/SecondaryButton';
import { Modal } from '../../../../components/common/modals/Modal';
import EditIcon from '../../../../components/icons/EditIcon';
import XMarkIcon from '../../../../components/icons/XmarkIcon';
import useUpdatePlayer from '../../../../hooks/api/players/useUpdatePlayer';

interface PlayersListEditButtonProps {
  playerId: string;
  playerEmail: string;
  playerName: string;
}

const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

const PlayersListEditButton = ({ playerId, playerEmail, playerName }: PlayersListEditButtonProps) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [email, setEmail] = useState(playerEmail);
  const [name, setName] = useState(playerName ?? '');
  const { updatePlayer, isLoading } = useUpdatePlayer();

  const trimmedEmail = email.trim();
  const trimmedName = name.trim();
  const hasChanges = trimmedEmail !== playerEmail || trimmedName !== (playerName ?? '');
  const isEmailValid = isValidEmail(trimmedEmail);
  const canSubmit = hasChanges && isEmailValid && !isLoading;

  const handleSubmit = async () => {
    if (!canSubmit) return;

    await updatePlayer({
      id: playerId,
      ...(trimmedEmail !== playerEmail && { email: trimmedEmail }),
      ...(trimmedName !== (playerName ?? '') && { name: trimmedName }),
    });
    setIsModalOpen(false);
  };

  return (
    <>
      <button
        aria-label={t('account.players.edit.buttonLabel')}
        className="p-3"
        onClick={e => {
          e.stopPropagation();
          setEmail(playerEmail);
          setName(playerName ?? '');
          setIsModalOpen(true);
        }}
      >
        <EditIcon />
      </button>

      <Modal isOpen={isModalOpen} setIsOpen={setIsModalOpen}>
        <div>
          <div className="p-6 relative border-b border-grey300">
            <h2 className="text-center text-[1.375rem] font-medium">{t('account.players.edit.title')}</h2>
            <div
              onClick={() => setIsModalOpen(false)}
              className="w-8 h-8 min-w-8 absolute top-6 right-6 lg:left-6 flex justify-center items-center cursor-pointer"
            >
              <XMarkIcon />
            </div>
          </div>

          <div className="p-6">
            <div className="flex flex-col gap-4 max-w-sm mx-auto">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className={`w-full border rounded-md px-3 py-2 text-sm ${
                    trimmedEmail && !isEmailValid ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {trimmedEmail && !isEmailValid && (
                  <p className="text-red-500 text-xs mt-1">{t('errorMessages.email')}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('account.players.edit.name')}</label>
                <input
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
              </div>
            </div>

            <div className="flex gap-6 mt-8 max-w-sm mx-auto">
              <SecondaryButton onClick={() => setIsModalOpen(false)}>{t('common.cancel')}</SecondaryButton>
              <PrimaryButton onClick={handleSubmit} disabled={!canSubmit} loading={isLoading}>
                {t('common.save')}
              </PrimaryButton>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default PlayersListEditButton;
